/**
 * Column — the bar's armed and working state, fused with the newest result into one lit column
 * over the dimmed wall (BRIEF §6, board ToileArmed / ToileArmedScratch). Caption · the take ·
 * counter · field · chip · Generate.
 *
 * The frame is BOX-FIT, not width-fixed (`frame.ts`): the picture that is coming is drawn at its
 * own ratio inside the bar's width x the column's own height BUDGET (plan
 * `2026_09_18_armed_frame_budget.md` § D1 — pixels the viewport leaves after the chrome, not a
 * fraction of it), so a landscape fills the bar and a portrait stands height-bounded and centred —
 * the ring and the glow are on the frame itself, so they wrap the picture and never the box it was
 * fitted in.
 *
 * The stack is two anchored pieces now (§ D2), not one centred column: `.pictureRegion` takes the
 * leftover height and centres the picture in it; `.dock` holds the bar at a fixed distance from the
 * viewport's own bottom edge, so nothing that opens above it — a fold, the muse strip growing the
 * bar taller (§ D6) — ever moves the bar itself. The counter stands BESIDE the frame when there is
 * room (§ D3, `frame.armedFrame`'s own `counterInFlow` decision), falling back into the flow under
 * it on a narrow viewport — split, on the desktop column, into its two natures (dev fix
 * 2026-09-18, `Counter.tsx`'s own header doc): arrows flanking the frame, a readout+pin pill inside
 * its corner. The terms card still overlays the frame's lower edge from inside `.dock` (§ D4); the
 * ratio row moved again the same day, off that overlay and into the bar's own slot (see the bar's
 * own comment below).
 *
 * **The caption** (board `WordsAsDoors`, "A" trimmed, validated 2026-09-18): the doors alone,
 * centred over the picture — no thumbnail, no "From:" prefix; a pack page is a landing, and there
 * is no reason to come back to it once on the wall. `rootDoors` holds the chain's words (a basis
 * tile's own chips, then the take on screen's typed direction — `chains.captionDoors`); `[]` falls
 * back to `rootLabel` as plain text (a chip-less tile, or nothing typed yet on a from-scratch
 * chain). A tap on a door folds the column exactly as every other return does and hands the word to
 * the wall's query — `onWordTap`, the caller's whole behaviour; this file only lays the words out.
 *
 * What stands here at all times is state; what acts sits in the bar (board `ColumnFurniture`). So
 * the permanent furniture is the caption, the picture, and — only above one step — the counter
 * (`Counter`), and the bar holds the direction field, the ratio as one chip that unfolds its pills
 * (`RatioFold`), and Generate. A house with `theme.pencil` (plan `2026_09_18_ovid_wall_polish.md` §
 * P3, Ovid) hides that field behind a pencil door instead: at rest the bar reads identity slot ·
 * pencil · verb, and the pencil opens the field in the slot's own place — Toile (`pencil`
 * unset) is unaffected, the field always on as before. The bar's own slot has exactly one occupant
 * at a time, never a stack: the identity panel wanting the bar's full attention
 * (`identityPanelOpen`, § D6 — museless, or the chip was tapped) reads identity strip · verb alone,
 * and the ratio row open (`ratioOpen`, dev fix 2026-09-18, superseding § D4's overlay for this row)
 * reads ratio pills · verb alone — field, ratio chip, pencil and identity chip all stand down for
 * whichever of the two is open, and opening one folds the other (they cannot both stand).
 *
 * The step on screen is the one the next Generate continues from (BRIEF § Regenerate), whether the
 * counter stepped to it or it is simply the newest; ‹ › and the left/right arrow keys are the same
 * step. Step 1 of a tile chain is the TILE itself (`chains.chainSteps`): stepped back to it, the
 * frame holds the tile's own picture at the tile's own ratio, and Generate twists the tile afresh.
 *
 * The ratio is askable at every basis (operator decision 2026-08-31, superseding "only from the
 * ghost does Toile ask"): the chip reads the shape on screen until the user picks another. Houses
 * with no picker (`hasRatioPicker: false`) have no chip.
 */
import {useEffect, useId, useRef, useState} from 'react'
import {Link} from 'react-router'
import {MAX_DIRECTION_CHARS, type Identity, type RatioKey} from '@core/wall/contract'
import {Notice} from '@core/ui/Notice'
import {
    chainTravel, fieldPlaceholder, identityArmable, noticeKindFor,
    type Aspect, type ChainStep, type VerbState,
} from './chains'
import {Counter, type PinDoorProps, type PinSlotState} from './Counter'
import {RatioChip, RatioRow} from './RatioFold'
import {WordDoors} from './WordDoors'
import type {Door} from './chains'
import {armedFrame, COLUMN_BOTTOM_CLEARANCE, COLUMN_TOP_CLEARANCE, COUNTER_FLANK_W, type Box} from './frame'
import {useFocusTrap} from './useFocusTrap'
import {coarsePointer} from './surface'
import {BarNote, BarVerb} from './AllowanceBar'
import {ShareDoor, ShareMenu, useFoldOnOutsidePress, useShareMenuId, type ShareDoorProps} from './ShareDoor'
import {chromeWords, ghostWords, pencilOn, verbWord, type BarAllowance, type HouseTheme} from './theme'
import {WorkingCell, type PreviewFrame} from './WorkingCell'
import styles from './Column.module.scss'

export type ColumnProps = {
    theme: HouseTheme
    /** The dialog's accessible name, and the caption's plain-text fallback when `rootDoors` is
     *  empty — always a full description (title · chips, or the typed direction), independent of
     *  what the caption actually draws. */
    rootLabel: string
    /** The caption's doors (`chains.captionDoors`) — `[]` renders `rootLabel` as plain text
     *  instead (a chip-less tile, or a from-scratch chain with nothing typed yet); when `rootHref`
     *  stands beside it, that text is itself the pack page's door (Narcis, whose tiles ship no
     *  chips yet — the pack link the pill caption would carry must not vanish with them). */
    rootDoors: readonly Door[]
    /** A door tap: fold the column (as every other return does) and hand the word to the wall's
     *  query. Called only from `rootDoors`' own chips — the caller's whole behaviour, this file
     *  lays the words out and nothing more. */
    onWordTap: (door: Door) => void
    /** The pack page behind the basis tile; `null` for a chain with no page (ghost, from-scratch,
     *  or a house whose surface has none). The caption's own discreet ↗ (board A, trimmed
     *  2026-09-18) — rendered only alongside `rootDoors`, never with the plain-text fallback. */
    rootHref: string | null
    resultUrl: string | null
    working: boolean
    /** The row `working` is about, the same two paint inputs `Tile.tsx`/`WorkingCell.tsx` take —
     *  `Wall.tsx`'s `armedView` derives them off the SAME row `working` reads (`chain?.running`
     *  un-stepped, or the stepped-to take when it is itself in flight): `queuePlace` (1-based, or 1
     *  once claimed/rendering) and `previewFrame` (the row's newest loaded frame). Both `null` while
     *  `working` is true but no row exists yet to derive them from (optimistic `firing`) — the
     *  shared cell paints the breath alone in that case, same as the tile's own fallback. */
    queuePlace?: number | null
    previewFrame?: PreviewFrame | null
    isScratch: boolean // no basis at all — the ghost / from-scratch path
    direction: string
    onDirectionChange: (value: string) => void
    /** What the chip reads and which pill is lit: the user's pick, or the shape on screen
     *  (`effectiveRatio`). */
    ratio: RatioKey
    onRatioChange: (ratio: RatioKey) => void
    onGenerate: () => void
    onClose: () => void
    /** Which of the house's two verb words to show — `Wall.tsx` computes this from the step on
     *  screen and the field (`chains.verbLabel`), held steady while a press is in flight so the
     *  word never flickers mid-render. This is the one place the two strings are chosen between. */
    verbState: VerbState
    /** This chain's steps, oldest first (`chains.chainSteps`) — the basis tile, then the takes made
     *  this session: the counter's `1..N`. */
    steps: readonly ChainStep[]
    /** The one on screen; `null` = the newest, which is where an arm opens. */
    selectedStepId: string | null
    onSelectStep: (id: string) => void
    /** The ratio of the picture that is coming (`chains.armedAspect`) — the frame is not a house
     *  portrait, it is this chain's own shape (N14), or the ratio just picked. */
    aspect: Aspect
    /** The viewport the frame is fitted into; the box itself is `frame.ts`'s, from this file's own
     *  `.bar` width. */
    viewport: Box
    /** The line for THIS chain, when there is one — scoped by the caller to the armed chain
     *  (re-review N4); the pill keeps its own vocabulary for everything else. */
    note?: string | null
    /** False while the sign-in card is over the column: one layer traps the keyboard at a time. */
    trapFocus?: boolean
    /** The terms gate's card (plan 2026_09_01_tos_gate.md §8.4), already rendered by the caller —
     *  `null` while the gate is closed. Overlays the frame's own lower edge; an open ratio row folds
     *  the moment this is set, so the two never stand at once even though they no longer share one
     *  DOM slot (the ratio row moved into the bar itself, this round). */
    consentSlot?: React.ReactNode
    /** Escape's innermost layer, ahead of the ratio row — called only while `consentSlot` is set. */
    onFoldConsent?: () => void
    /** The pin door beside the counter (plan `2026_09_04_wall_pin.md`) — `Wall.tsx`'s own gate
     *  (`armedView.pinnableTake`) decides when this is non-null; `Counter` renders nothing for it
     *  otherwise. */
    pin?: PinDoorProps | null
    /** Which of the pin slot's three states to draw (`Counter.tsx`'s own `PinSlotState` doc) —
     *  `Wall.tsx` computes this alongside `pin` from the same `armedView`, so a basis step or a
     *  queued/rendering take still stands the idle bookmark rather than an empty slot that shifts
     *  the counter's own width across steps. */
    pinSlot?: PinSlotState
    /** The Share door on the frame (`useShare.ts`; `ShareDoor.tsx`) — non-null whenever a picture is on
     *  screen. Never drawn while the muse strip's
     *  preview stands in the frame, or while a take is at work. */
    share?: ShareDoorProps | null
    /** The current identity (`/mine`'s own field) — `undefined` for a house that never wired one
     *  (Toile: `identityArmable(undefined)` reads armable, unchanged behaviour). Read only through
     *  `identityArmable` and handed verbatim to `theme.identityPanel` (`@core/wall/ui/theme.ts`'s
     *  `IdentityPanelProps`, plan `2026_09_17_ovid_wall_verbs.md` § Decided 5). */
    identity?: Identity
    /** The panel's own write — see `IdentityPanelProps.onIdentityChange`'s own doc. Required only
     *  when `theme.identityPanel` is set; Toile passes neither. */
    onIdentityChange?: (identity: Identity) => void
    /** Bumped once per `/continue` refusal for a missing identity (409 `identity_required`,
     *  `Wall.tsx`'s own `fire`) — translated into `IdentityPanelProps.open` on the transition
     *  (plan `2026_09_18_ovid_wall_polish.md` § P4: the boolean itself now lives here, not in the
     *  panel). This file assigns no other meaning to the number. */
    identityOpenSignal?: number
    /** `Wall`'s door to the intake overlay, handed to the identity panel (`IdentityPanelProps.onOpenIntake`);
     *  absent when the house mounts no intake. */
    onOpenIntake?: () => void
    /** What the reader's picture allowance makes of the bar (`HouseTheme.allowanceBar`): a note in the bar's spacer,
     *  and, when spent, a verb of its own with the pencil gone. All inside the bar's row. */
    allowanceBar?: BarAllowance | null
    /** A visitor's quick look (plan `2026_09_18_ovid_wall_polish.md` § P5): the pencil calls
     *  `onGenerate` instead of toggling the field open — there is nothing to type into, since
     *  Generate always leads to the sign-in card, never a `/continue`. `false` for every member
     *  column, which keeps the pencil's own field door exactly as P3 built it. */
    locked?: boolean
    /** Suppresses the caption entirely — a visitor's locked column carries no estate text to name
     *  (`AnonTile` has no title/chips/slug at all); `rootLabel` is still read for the dialog's own
     *  `aria-label`. `false` everywhere else, unchanged markup. */
    hideFrom?: boolean
}

export function Column({
    theme, rootLabel, rootDoors, onWordTap, rootHref, resultUrl, working, queuePlace, previewFrame, isScratch,
    direction, onDirectionChange, ratio, onRatioChange, onGenerate, onClose, verbState,
    steps, selectedStepId, onSelectStep, aspect, viewport,
    note, trapFocus = true, consentSlot = null, onFoldConsent, pin = null, pinSlot, share = null,
    identity, onIdentityChange, identityOpenSignal = 0, onOpenIntake, allowanceBar = null, locked = false, hideFrom = false,
}: ColumnProps) {
    const ghostArmed = ghostWords(theme).armed
    // The verb disarms whenever the identity says so (Ovid, museless) — a pure readout of the
    // shared `Identity` shape, so this file carries no muse-specific knowledge of its own.
    const armable = identityArmable(identity)
    const barWidth = theme.armed.barWidth
    const {frame: fitted, counterInFlow} = armedFrame(aspect, viewport, barWidth)
    const frame = {width: fitted.width, height: fitted.height}
    // The frame the muse strip stands in for (§ D6) while it is open — `null` renders the chain's
    // own picture as ever; `MuseStrip`'s own `onPreview` is the only thing that ever sets this,
    // called with `null` again on close/unmount, so nothing here has to guess when to clear it.
    const [identityPreviewUrl, setIdentityPreviewUrl] = useState<string | null>(null)
    // C2: the field's placeholder differs by whether there is a picture on screen to change.
    const displayedStep = chainTravel(steps, selectedStepId).step
    const chrome = chromeWords(theme)
    const placeholderText = fieldPlaceholder(displayedStep, theme.verbs.placeholder)
    const [ratioOpen, setRatioOpen] = useState(false)
    const ratioRowId = useId()
    // The Share menu: one layer, folded before the ratio row or the column on Escape, and by a press outside
    // it. Opening it folds the ratio row and the identity strip, so no two choices stand at once.
    const [shareOpen, setShareOpen] = useState(false)
    const shareMenuId = useShareMenuId()
    const shareDrawn = share !== null && !identityPreviewUrl && !working
    const shareMenuOpen = shareDrawn && shareOpen
    const openSlotRef = useRef<HTMLElement>(null)
    const scrimRef = useRef<HTMLDivElement>(null)
    const scrimPressFoldedRef = useRef(false)
    // One fold at a time (plan §8.4): the consent card overlays the frame's lower edge and the ratio
    // row now unfolds in the bar's own slot (this round, superseding § D4 for the ratio row — see
    // this file's header), so opening the card still folds an open row rather than the two
    // stacking.
    useEffect(() => {
        if (consentSlot) {
            setRatioOpen(false)
            setShareOpen(false)
        }
    }, [consentSlot])
    // N8: the column is a layer over a dimmed wall, so it takes the keyboard with it — focus opens
    // on the field (the column's one verb is typing a direction and pressing Generate), Tab stays
    // inside, Escape closes, and closing hands focus back to the tile that opened it.
    const stageRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // The identity strip's own open/closed state (plan `2026_09_18_ovid_wall_polish.md` § P4) —
    // lifted here from `MuseStrip`'s own local `expanded`; `Wall.tsx`'s refusal counter
    // (`identityOpenSignal`) still arrives as an external prop (a counter, not a boolean, is the
    // right shape for "fire even on a repeated identical refusal" — kept rather than rebuilt, per
    // the plan's own allowance), translated into this boolean on the transition alone so a second
    // refusal after the reader closed the strip again re-opens it.
    const [identityOpen, setIdentityOpen] = useState(false)
    const lastIdentitySignal = useRef(identityOpenSignal)
    useEffect(() => {
        if (identityOpenSignal !== lastIdentitySignal.current) {
            lastIdentitySignal.current = identityOpenSignal
            setIdentityOpen(true)
        }
    }, [identityOpenSignal])
    // One call now, not two (plan `2026_09_18_armed_frame_budget.md` § D6: "there is one placement:
    // the bar's slot") — `theme.identityPanel` decides its own shape (the closed chip or the open
    // strip) from `open`/`identity` alone; this file only decides WHERE the return value lands.
    // `identityPanelOpen` is the generic, muse-agnostic readout of "the panel wants the bar's full
    // width right now" — unarmed (museless) or explicitly asked for, the same two conditions
    // `MuseStrip`'s own `showStrip` reads, restated here without any muse-specific field.
    const [barHeld, setBarHeld] = useState(false)
    const identityContent = theme.identityPanel && identity && onIdentityChange
        ? theme.identityPanel({
              identity, onIdentityChange, open: identityOpen, onOpenChange: setIdentityOpen,
              onPreview: setIdentityPreviewUrl, onOpenIntake, onHoldBar: setBarHeld,
          })
        : null
    const identityPanelOpen = Boolean(theme.identityPanel) && (!armable || identityOpen)

    useFoldOnOutsidePress({
        open: shareMenuOpen, scrimRef, scrimFolded: scrimPressFoldedRef, fold: () => setShareOpen(false),
    })

    // The way out of whichever choice stands in the bar's slot — the ratio row or the identity strip:
    // a press anywhere outside it folds it and changes nothing (the verb included, whose own click
    // then runs as usual). Learned from the press itself, not from focus: watched in WebKit on dev,
    // 2026-09-18, a mouse press on a pill moves focus to the stage container, so a fold-on-focus-out
    // rule unmounted the row between the press and its click and no ratio could be picked.
    useEffect(() => {
        if (!ratioOpen && !identityOpen) return
        function onPointerDown(e: PointerEvent) {
            const slot = openSlotRef.current
            if (slot && !slot.contains(e.target as Node)) {
                setRatioOpen(false)
                setIdentityOpen(false)
                // One layer at a time, the innermost first — the rule Escape already follows: a press
                // on the dimmed wall folds the open choice and stops there; the next one returns.
                if (e.target === scrimRef.current) scrimPressFoldedRef.current = true
            }
        }
        document.addEventListener('pointerdown', onPointerDown, true)
        return () => document.removeEventListener('pointerdown', onPointerDown, true)
    }, [ratioOpen, identityOpen])

    // The ratio row and the identity panel now share the bar's one slot (this round's own decision,
    // below): opening either folds the other. Reachable mainly through `identityOpenSignal` — a
    // server refusal can flip `identityOpen` true while the ratio row happens to be open, which
    // would otherwise leave two "open" flags true with only one slot to draw either in.
    useEffect(() => {
        if (ratioOpen) setIdentityOpen(false)
    }, [ratioOpen])
    useEffect(() => {
        if (identityOpen) setRatioOpen(false)
    }, [identityOpen])
    useEffect(() => {
        if (shareMenuOpen) {
            setRatioOpen(false)
            setIdentityOpen(false)
        }
    }, [shareMenuOpen])
    useEffect(() => {
        if (ratioOpen || identityOpen) setShareOpen(false)
    }, [ratioOpen, identityOpen])
    // Folding the ratio row returns focus to the chip that opened it. The chip is unmounted while
    // the row is open (below: the bar shows the pills alone), so `RatioChip`'s own open/close focus
    // effect (`RatioFold.tsx`) never sees the transition — mounting is all it would see, and mounting
    // happens on EVERY close, including the very first render (where the trap's own initial focus,
    // not this, should decide where the keyboard goes). So the transition is watched here instead,
    // the same FOLDED-TO-OPEN idiom as the pencil field's own effect below, kept the other way round.
    const wasRatioOpen = useRef(ratioOpen)
    useEffect(() => {
        if (wasRatioOpen.current && !ratioOpen) {
            stageRef.current?.querySelector<HTMLElement>('[data-ratio-chip="true"]')?.focus()
        }
        wasRatioOpen.current = ratioOpen
    }, [ratioOpen])

    // The pencil's own field (plan § P3) — folded at rest, whether it was ever opened; a chain
    // handed a direction already typed (e.g. armed fresh with words carried over) starts open, since
    // "typed words keep the field open" is the rule at every point, not only after the fact.
    const [fieldOpen, setFieldOpen] = useState(() => Boolean(theme.pencil) && direction.trim().length > 0)
    const wasFieldOpen = useRef(fieldOpen)
    useEffect(() => {
        // Focus only on the FOLDED-TO-OPEN transition (the same idiom `RatioFold.tsx`'s `RatioChip`
        // uses for its own open/close focus return) — never on mount, where the trap's own initial
        // focus already decided where the keyboard goes (see `initialFocusRef` below).
        if (theme.pencil && fieldOpen && !wasFieldOpen.current) {
            inputRef.current?.focus({preventScroll: true})
        }
        wasFieldOpen.current = fieldOpen
    }, [theme.pencil, fieldOpen])

    function togglePencil() {
        if (!fieldOpen) {
            setFieldOpen(true)
            return
        }
        // Escape and a blur both fold the field back only while it is empty (BRIEF's own rule,
        // plan § P3) — the pencil itself honours the identical invariant: typed words keep the
        // field open, so a second press with words in it does nothing.
        if (direction.trim() === '') setFieldOpen(false)
    }
    // A coarse pointer (touch) opening on the field summons the on-screen keyboard inside the tap's
    // own activation window, which is what the reveal-scroll of the return-gesture fix chases — read
    // once, client-only (SSR never renders an open column). Column only ever mounts on the column
    // surface (`Wall.tsx`'s `isSheetSurface()` gives the Sheet to every coarse pointer at or under
    // 1024px — code-quality review F5, correcting this comment's earlier width claim, which named
    // widths the Sheet has taken since `51b459128`), so the one case this guard still meets is a
    // coarse pointer ABOVE that width — a tablet in landscape. Desktop is untouched: a mouse has no
    // `pointer: coarse` match, and focus opens in the field as it always has.
    const [coarseInput] = useState(coarsePointer)
    // Escape answers the innermost layer first: the consent card, then the identity panel or the
    // ratio row (never both open at once — the two effects above see to that; identity checked
    // first purely as the tie-break if that invariant were ever broken), then the column itself. The
    // trap listens on the document in the capture phase, so no handler inside either fold could take
    // the key back — the layering has to be decided here, where every state is known.
    useFocusTrap({
        active: trapFocus,
        containerRef: stageRef,
        onEscape: () => {
            if (consentSlot) {
                onFoldConsent?.()
                return
            }
            if (shareMenuOpen) {
                setShareOpen(false)
                return
            }
            // The muse strip, if the reader opened it (§ D6) — a no-op while museless (the strip
            // stands regardless, `identityPanelOpen`'s own `!armable` half), same as the chip's own
            // toggle only ever closing what it opened.
            if (identityOpen) {
                setIdentityOpen(false)
                return
            }
            if (ratioOpen) {
                setRatioOpen(false)
                return
            }
            // The pencil's own field, if open — folds it back only while it is empty (BRIEF's rule,
            // plan § P3), the same invariant the pencil button and a blur both honour; words in it
            // mean this Escape closes the column instead, same as any other house.
            if (theme.pencil && fieldOpen && direction.trim() === '') {
                setFieldOpen(false)
                return
            }
            onClose()
        },
        // A pencil house with the field folded has no field in the DOM to focus yet (it opens only
        // on a pencil tap) — `undefined` falls back to the trap's own `focusables()[0]`, the first
        // real control in the bar. Toile (`!theme.pencil`) and a pencil house that opened already
        // holding words both read exactly as before: the field, focused at once.
        initialFocusRef: coarseInput ? stageRef : (!theme.pencil || fieldOpen ? inputRef : undefined),
    })

    // ← → step the chain too, wherever focus is in the column except the field, whose own caret
    // those keys move (board rule: the number pair is display; only ‹ › and these step). On the
    // layer's own node rather than a JSX handler: the column is a container, not a control, and
    // giving it an interaction prop would be a lie about what it is.
    useEffect(() => {
        const node = stageRef.current
        if (!node) return
        function onKey(e: KeyboardEvent) {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
            if (e.target instanceof HTMLInputElement) return
            const {prev, next} = chainTravel(steps, selectedStepId)
            const stepped = e.key === 'ArrowLeft' ? prev : next
            if (!stepped) return
            e.preventDefault()
            onSelectStep(stepped.id)
        }
        node.addEventListener('keydown', onKey)
        return () => node.removeEventListener('keydown', onKey)
    }, [steps, selectedStepId, onSelectStep])

    return (
        <div
            className={styles.stage}
            ref={stageRef}
            role="dialog"
            aria-modal="true"
            aria-label={rootLabel}
            tabIndex={-1}
            style={{
                outline: 'none',
                '--wall-bar-w': `${barWidth}px`,
                '--wall-flank-w': `${COUNTER_FLANK_W}px`,
                '--wall-col-top': `${COLUMN_TOP_CLEARANCE}px`,
                '--wall-col-bottom': `${COLUMN_BOTTOM_CLEARANCE}px`,
            } as React.CSSProperties}
        >
            <div
                ref={scrimRef}
                className={styles.scrim}
                onClick={() => {
                    if (scrimPressFoldedRef.current) {
                        scrimPressFoldedRef.current = false
                        return
                    }
                    onClose()
                }}
            />

            <div className={styles.esc}>
                <span>{chrome.back.lead}</span>
                <span className={styles.key}>esc</span>
                <span className={styles.sep}>·</span>
                <span>{chrome.back.desktop}</span>
            </div>

            <div className={styles.col}>
                <div className={styles.pictureRegion}>
                    {!hideFrom && (
                        rootDoors.length > 0 ? (
                            <WordDoors
                                doors={rootDoors}
                                onTap={onWordTap}
                                href={rootHref}
                                className={styles.caption}
                                size="caption"
                            />
                        ) : rootHref ? (
                            <Link to={rootHref} className={styles.plain}>{rootLabel} ↗</Link>
                        ) : (
                            <span className={styles.plain}>{rootLabel}</span>
                        )
                    )}

                    <div className={styles.frameWrap}>
                        {identityPreviewUrl ? (
                            <div className={styles.result} style={frame}>
                                <img src={identityPreviewUrl} alt="" />
                            </div>
                        ) : isScratch && !resultUrl ? (
                            <div className={`${styles.result} ${styles.resultScratch}`} style={frame}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 3 a2.83 2.83 0 0 1 4 4 L7.5 20.5 L2 22 L3.5 16.5 Z" />
                                </svg>
                                {direction ? (
                                    <span>{direction}</span>
                                ) : (
                                    <>
                                        <span>{ghostArmed.own}</span>
                                        {ghostArmed.rule && <span className={styles.scratchRule}>{ghostArmed.rule}</span>}
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className={`${styles.result} ${working ? styles.resultWorking : ''}`} style={frame}>
                                {working ? (
                                    <WorkingCell queuePlace={queuePlace} previewFrame={previewFrame} />
                                ) : resultUrl ? (
                                    <img src={resultUrl} alt="" />
                                ) : null}
                            </div>
                        )}

                        {shareDrawn && share && (
                            <>
                                <ShareDoor door={share} surface="column" open={shareMenuOpen} onOpenChange={setShareOpen} menuId={shareMenuId} />
                                {shareMenuOpen && (
                                    <ShareMenu door={share} surface="column" onOpenChange={setShareOpen} menuId={shareMenuId} variant="float" />
                                )}
                            </>
                        )}

                        {/* Both natures of the counter hide while the muse strip stands a preview in the
                            frame's place (§ D6) — there is no chain step on screen to travel, and a pill
                            over someone else's preview would read as belonging to it. */}
                        {!identityPreviewUrl && !counterInFlow && (
                            <Counter
                                layout="split"
                                steps={steps} selectedStepId={selectedStepId} onSelectStep={onSelectStep}
                                pin={pin} pinSlot={pinSlot} pinWords={chrome.pin}
                            />
                        )}
                    </div>

                    {/* The fallback row stands at the FRAME's own width, floored at 220 — not the
                        bar's (`.pictureRegion`'s own content box, which `.count`'s `width: 100%`
                        would otherwise resolve against) — so the travel group centres under the
                        picture it belongs to rather than under the wider bar (F3, dev fix 2026-09-18,
                        `Counter.module.scss`'s own header). */}
                    {!identityPreviewUrl && counterInFlow && (
                        <div style={{width: Math.max(frame.width, 220)}}>
                            <Counter
                                steps={steps} selectedStepId={selectedStepId} onSelectStep={onSelectStep}
                                pin={pin} pinSlot={pinSlot} pinWords={chrome.pin}
                            />
                        </div>
                    )}
                </div>

                <div className={styles.dock}>
                    {consentSlot && (
                        <div className={styles.consentSlot} data-wall-return-exempt="true">{consentSlot}</div>
                    )}

                    <div className={styles.bar}>
                        {ratioOpen ? (
                            <div
                                ref={openSlotRef as React.RefObject<HTMLDivElement>}
                                className={styles.ratioSlot}
                                data-wall-return-exempt="true"
                            >
                                <RatioRow
                                    id={ratioRowId}
                                    keys={theme.ratioKeys}
                                    ratio={ratio}
                                    onPick={(key) => {
                                        onRatioChange(key)
                                        setRatioOpen(false)
                                    }}
                                    className={styles.ratioPills}
                                />
                            </div>
                        ) : identityPanelOpen ? (
                            <span ref={openSlotRef} className={styles.idOpen} data-wall-return-exempt="true">{identityContent}</span>
                        ) : theme.pencil && (!fieldOpen || allowanceBar?.verb) ? (
                            <span className={styles.idChip} data-wall-return-exempt="true">
                                {identityContent}
                                {allowanceBar && <BarNote bar={allowanceBar} />}
                            </span>
                        ) : (
                            <span className={styles.slot} data-wall-return-exempt="true">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={direction}
                                    onChange={(e) => onDirectionChange(e.target.value)}
                                    placeholder={placeholderText}
                                    className={styles.input}
                                    maxLength={MAX_DIRECTION_CHARS}
                                    aria-label={placeholderText}
                                    onBlur={
                                        theme.pencil
                                            ? () => {
                                                  // The same fold-when-empty rule Escape and the pencil
                                                  // both honour (BRIEF's own rule, plan § P3).
                                                  if (direction.trim() === '') setFieldOpen(false)
                                              }
                                            : undefined
                                    }
                                    onKeyDown={(e) => {
                                        // An IME's confirming Enter is not a Generate: `isComposing` says
                                        // the keystroke belongs to the composition, and cancelling it below
                                        // would swallow the confirm.
                                        if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
                                        // Cancelled, or the browser runs the key's DEFAULT action too —
                                        // and by then focus may have left this field. Operator-verified
                                        // defect, 2026-08-31: signed out, this Enter opens the sign-in
                                        // overlay, whose focus trap moves focus to the card's first
                                        // control (SignInCard's Google submit button); the uncancelled
                                        // keydown then produces a `keypress`, which the browser dispatches
                                        // to the NEWLY focused element — and a button's default handler
                                        // for Enter is a simulated click, so `POST /auth/google` fired
                                        // before the card was ever read (dev access log, three times
                                        // between 18:32 and 18:34 UTC). Cancelling the keydown suppresses
                                        // the keypress, so nothing but this handler acts on the key.
                                        e.preventDefault()
                                        // Museless-disarmed (Ovid): an uncancelled Enter has nothing to
                                        // fire — the button below is `disabled` for the identical reason,
                                        // this is the same rule for the keyboard path the click never takes.
                                        if (armable) onGenerate()
                                    }}
                                />
                            </span>
                        )}
                        {!ratioOpen && !identityPanelOpen && theme.hasRatioPicker && (
                            <RatioChip
                                rowId={ratioRowId}
                                ratio={ratio}
                                open={ratioOpen}
                                onToggle={() => setRatioOpen(true)}
                            />
                        )}
                        {!ratioOpen && !identityPanelOpen && !allowanceBar?.verb && pencilOn(theme, identity) && (
                            <button
                                type="button"
                                className={styles.pencil}
                                aria-label={chrome.pencil}
                                // Not a toggle in locked mode (P5) — a plain door to sign-in, so it
                                // carries no pressed state at all.
                                aria-pressed={locked ? undefined : fieldOpen}
                                // Locked (P5): the pencil is a second door to sign-in, the same wire the
                                // verb carries — there is nothing to type into a chain a visitor cannot
                                // fire.
                                onClick={locked ? onGenerate : togglePencil}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 3 a2.83 2.83 0 0 1 4 4 L7.5 20.5 L2 22 L3.5 16.5 Z" />
                                </svg>
                            </button>
                        )}
                        {!barHeld && (allowanceBar?.verb ? (
                            <BarVerb verb={allowanceBar.verb} className={styles.verb} />
                        ) : (
                            <button type="button" className={styles.verb} onClick={onGenerate} disabled={!armable}>
                                {verbWord(theme, verbState, identity, working)}
                            </button>
                        ))}
                    </div>
                </div>

                {note && <Notice kind={noticeKindFor(note)} className={styles.note} ttlMs={0}>{note}</Notice>}
            </div>
        </div>
    )
}
